import React, { useEffect, useRef } from "react";
import { Container, Card, Form, Button, Spinner, Alert, Modal } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiX, FiSend, FiEye, FiDownload } from "react-icons/fi";
import type { Row as TanRow } from "@tanstack/react-table";

import ReportTreeTable from "@core/components/ReportTreeTable";
import type { ReportMetricRow } from "@core/components/ReportTreeTable";
import {
  fetchPastorReportAsync,
  type PastorChurchEntry,
  type PastorChurchReport,
  type PastorDistrictReport,
  type PastorNationReport,
  type PaginatedPastorResponse,
} from "@core/reports/api/fetchPastorReport";
import {
  buildPastorDistrictTree,
  buildPastorNationTree,
  pastorTotalsToColumnsSpec,
  pastorTotalsToFieldMap,
} from "@core/reports/adapters/pastorTreeAdapters";
import { requestReportReviewAsync } from "@core/reports/api/requestReportReview";
import RequestReviewModal from "@core/reports/components/RequestReviewModal";
import { requestReportApproveAsync } from "@core/reports/api/requestReportApprove";
import { requestDepartmentReportSaveAsync } from "@core/reports/api/requestDepartmentReportSave";
import { requestDepartmentReportSubmitAsync } from "@core/reports/api/requestDepartmentReportSubmit";
import { processDepartmentReportAsync } from "@core/reports/api/processDepartmentReport";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import ReportActionsPanel from "@core/components/ReportActionsPanel";
import PageBreadcrumb from "@core/components/PageBreadcrumb";
import { useEntityBasePath } from "@core/hooks/useEntityBasePath";
import { pastorReportsListToViewTrail } from "@core/navigation/pageBreadcrumbTrails";
import NoDepartmentReportNotice from "@core/reports/components/NoDepartmentReportNotice";
import { useDepartmentReportMedia } from "@core/reports/hooks/useDepartmentReportMedia";
import DepartmentReportMediaPanel from "@core/reports/components/DepartmentReportMediaPanel";
import ChildMediaPopover from "@core/reports/components/ChildMediaPopover";
import ReportPreviewModal from "@core/reports/components/ReportPreviewModal";
import MinistersReportView from "../components/MinistersReportView";
import type { DepartmentReportMediaItem, DepartmentReportDocument, ReportProcessResult } from "@core/reports/api/types";
import {
  canApproveDepartmentReport,
  canRequestChildDepartmentReportReview,
  canRequestDepartmentReportReview,
  canDownloadReport,
  getApprovalNotes,
  getNeedsAttentionReason,
  parseNeedsAttentionDetails,
  getRequestReviewCopy,
  getWorkflowStatusLabel,
  isAncestorOpenForChildReview,
  resolveDepartmentReportDisplayStatus,
  type ReportProcessResults,
} from "@core/reports/utils/reportWorkflow";
import { useLeadershipReportPermissions } from "@core/reports/hooks/useLeadershipReportPermissions";
import { useSelectedReportTag } from "@core/reports/hooks/useSelectedReportTag";
import { buildPrintHeaderFields, buildReportFileBase } from "@core/reports/utils/reportPrintMeta";
import { surfaceDeptActionError } from "@core/reports/utils/surfaceDeptActionError";
import { parseReportViewSearch } from "@core/reports/constants/reportSpaQueryParams";
import { useAuthStore } from "@features/auth/store/authStore";

type Scope = "district" | "nation";
type PastorAggData = PastorDistrictReport | PastorNationReport;

export default function PastorAggregatedReportPage() {
  const { districtId: urlDistrictId, nationId: urlNationId } = useParams<{ districtId?: string; nationId?: string }>();
  const [searchParams] = useSearchParams();

  const scope: Scope = urlNationId ? "nation" : "district";
  const entityId = Number(urlNationId ?? urlDistrictId) || 0;

  const { departmentId, reportTagId } = parseReportViewSearch(searchParams);

  const [page, setPage] = React.useState(1);
  const [perPage] = React.useState(20);

  const [resp, setResp] = React.useState<PaginatedPastorResponse<PastorAggData> | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Workflow state
  const [savingDraft, setSavingDraft] = React.useState(false);
  const [submittingDept, setSubmittingDept] = React.useState(false);
  const [validatingDept, setValidatingDept] = React.useState(false);
  const [deptActionError, setDeptActionError] = React.useState<string | null>(null);
  const [validationResult, setValidationResult] = React.useState<ReportProcessResult | null>(null);
  const [deptNotes, setDeptNotes] = React.useState("");
  const notesRef = useRef<HTMLTextAreaElement>(null);

  // Child-row request-review modal (district at nation scope; church at district scope)
  const [showRowReviewModal, setShowRowReviewModal] = React.useState(false);
  const [reviewTarget, setReviewTarget] = React.useState<{ kind: "church" | "district"; id: number; name: string; reportId: number | null } | null>(null);
  const [requestingRowReview, setRequestingRowReview] = React.useState(false);
  const [rowReviewError, setRowReviewError] = React.useState<string | null>(null);

  // Parent report request-review modal
  const [showParentReviewModal, setShowParentReviewModal] = React.useState(false);
  const [requestingParentReview, setRequestingParentReview] = React.useState(false);
  const [parentReviewError, setParentReviewError] = React.useState<string | null>(null);

  // Approve modal
  const [showApproveModal, setShowApproveModal] = React.useState(false);
  const [approveNotes, setApproveNotes] = React.useState("");
  const [approving, setApproving] = React.useState(false);
  const [approveError, setApproveError] = React.useState<string | null>(null);

  // Eyeball preview modal (Pastor variant)
  const [previewChurch, setPreviewChurch] = React.useState<PastorChurchReport | null>(null);

  const canLoad = entityId > 0 && reportTagId > 0;

  const load = React.useCallback(async (): Promise<void> => {
    if (!canLoad) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPastorReportAsync<PastorAggData>(
        scope === "nation"
          ? { scope: "nation", nationId: entityId, reportTagId, page, perPage }
          : { scope: "district", districtId: entityId, reportTagId, page, perPage }
      );
      if (result.isSuccess()) {
        setResp(result.getData());
      } else {
        setError(result.getError()?.detail || "Failed to fetch Pastor report");
        setResp(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setResp(null);
    } finally {
      setLoading(false);
    }
  }, [scope, entityId, reportTagId, page, perPage, canLoad]);

  React.useEffect(() => {
    if (canLoad) load();
  }, [load, canLoad]);

  const data = resp?.data;
  const meta = data?.meta;
  const submission = meta?.department_submission ?? null;
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const requestReviewCopy = getRequestReviewCopy(
    currentUserId,
    submission?.submitted_by
  );
  const requestReviewLabel = requestReviewCopy.actionLabel;
  const requestReviewNoun = requestReviewCopy.noun;
  const departmentReportId: number | null = submission?.department_report_id ?? null;
  const statusName = resolveDepartmentReportDisplayStatus(
    departmentReportId,
    submission?.report_status_name ?? null
  );
  const processResults = (meta as { process_results?: ReportProcessResults | null } | undefined)
    ?.process_results;
  const needsAttentionReason = getNeedsAttentionReason(statusName, processResults);
  const needsAttentionDetails = parseNeedsAttentionDetails(processResults);
  const approvalNotesDisplay = getApprovalNotes(submission?.approval_notes, processResults);

  const { permissionFlags, actionsVisible } = useLeadershipReportPermissions({
    scope,
    scopeId: entityId,
    departmentId,
    statusName,
    submittedBy: submission?.submitted_by,
    enabled: canLoad,
  });

  const { tagIsOpen, tagStatusName } = useSelectedReportTag({
    districtId: scope === "district" ? entityId || undefined : undefined,
    nationId: scope === "nation" ? entityId || undefined : undefined,
    reportTagId,
    enabled: canLoad,
  });

  const canEdit = permissionFlags.fieldsEditable && tagIsOpen;
  const { showSave, showSubmit, showValidate, showApprove, showUploadMedia, showReview } = permissionFlags;
  const showSaveAction = showSave && tagIsOpen;
  const showSubmitAction = showSubmit && tagIsOpen;
  const showUploadMediaAction = showUploadMedia && tagIsOpen;
  const canRemoveMedia = canEdit && permissionFlags.canRemoveMedia;
  const showRowReview = showReview && actionsVisible;
  const parentOpenForChildReview = isAncestorOpenForChildReview(departmentReportId, statusName);

  useEffect(() => {
    if (savingDraft || submittingDept) return;
    setDeptNotes(String(submission?.notes ?? ""));
  }, [submission?.notes, submission, savingDraft, submittingDept]);

  React.useEffect(() => {
    setDeptActionError(null);
    setValidationResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, departmentId, reportTagId]);

  const existingMedia = React.useMemo<DepartmentReportMediaItem[]>(
    () => (Array.isArray(meta?.media) ? (meta?.media as DepartmentReportMediaItem[]) : []),
    [meta]
  );
  const mediaRequirements = React.useMemo<DepartmentReportDocument[]>(() => {
    const value = meta?.documents;
    return Array.isArray(value) ? value : [];
  }, [meta]);

  const media = useDepartmentReportMedia({
    scope,
    entityId,
    departmentId,
    reportTagId,
    notes: deptNotes,
    departmentReportId,
    existingMedia,
    editable: showUploadMediaAction,
    onChanged: async () => { await load(); },
  });

  React.useEffect(() => {
    media.clearUploadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, departmentId, reportTagId]);

  // Index church entries by id so the eyeball can render the full Minister's Report from the payload.
  const churchEntryById = React.useMemo(() => {
    const map = new Map<number, PastorChurchEntry>();
    if (!data) return map;
    if ("churches" in data) {
      for (const c of data.churches ?? []) map.set(c.church_id, c);
    } else if ("districts" in data) {
      for (const d of data.districts ?? []) for (const c of d.churches ?? []) map.set(c.church_id, c);
    }
    return map;
  }, [data]);

  const columnsSpec = React.useMemo(() => pastorTotalsToColumnsSpec(data?.totals), [data]);
  const footerTotals = React.useMemo(() => pastorTotalsToFieldMap(data?.totals), [data]);
  const treeData = React.useMemo<ReportMetricRow[]>(() => {
    if (!data) return [];
    return "districts" in data
      ? buildPastorNationTree(data.districts)
      : buildPastorDistrictTree(data.churches);
  }, [data]);

  const openPreview = (churchId: number) => {
    const church = churchEntryById.get(churchId);
    if (!church) return;
    setPreviewChurch({
      meta: {
        ...(meta as any),
        church_id: church.church_id,
        church_name: church.church_name,
        department_submission: church.department_submission,
        media: church.media ?? [],
      },
      status: church.status,
      finances: church.finances,
      statistics: church.statistics,
      assessment: church.assessment,
    });
  };

  const handleRowRequestReview = async ({ reason }: { reason: string }) => {
    if (!reviewTarget?.reportId) {
      setRowReviewError(`Missing department report id for the selected ${reviewTarget?.kind ?? "row"}.`);
      return;
    }
    setRequestingRowReview(true);
    setRowReviewError(null);
    try {
      const result = await requestReportReviewAsync(
        reviewTarget.kind === "district"
          ? {
              scope: "district",
              districtId: reviewTarget.id,
              departmentId,
              departmentReportId: reviewTarget.reportId,
              notes: reason.slice(0, 255),
            }
          : {
              scope: "church",
              churchId: reviewTarget.id,
              departmentId,
              departmentReportId: reviewTarget.reportId,
              notes: reason.slice(0, 255),
            }
      );
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to request review.");
      setShowRowReviewModal(false);
      await load();
    } catch (e) {
      setRowReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingRowReview(false);
    }
  };

  const ownArgs = scope === "nation"
    ? { scope: "nation" as const, nationId: entityId }
    : { scope: "district" as const, districtId: entityId };

  const handleSaveDraft = async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSavingDraft(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSaveAsync({ ...ownArgs, departmentId, reportTagId, notes: deptNotes, existingMedia: media.captionPatches } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to save draft.");
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to save draft.",
        setDeptActionError
      );
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitDepartmentReport = async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSubmittingDept(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSubmitAsync({ ...ownArgs, departmentId, reportTagId, notes: deptNotes, existingMedia: media.captionPatches } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to submit report.");
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to submit report.",
        setDeptActionError
      );
    } finally {
      setSubmittingDept(false);
    }
  };

  const handleValidateDepartmentReport = async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setValidatingDept(true);
    try {
      const result = await processDepartmentReportAsync({ ...ownArgs, departmentId, reportTagId } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to validate report.");
      setValidationResult(result.getData());
      await load();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to validate report.",
        setDeptActionError
      );
    } finally {
      setValidatingDept(false);
    }
  };

  const handleApprove = async () => {
    if (!canLoad || !departmentReportId) {
      setApproveError("Missing department report id. Cannot approve report.");
      return;
    }
    setApproving(true);
    setApproveError(null);
    try {
      const result = await requestReportApproveAsync({ ...ownArgs, departmentId, departmentReportId, notes: approveNotes ?? "" } as any);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to approve report.");
      setShowApproveModal(false);
      await load();
    } catch (e) {
      setApproveError(e instanceof Error ? e.message : "Failed to approve report.");
    } finally {
      setApproving(false);
    }
  };

  const handleParentRequestReview = async ({ reason }: { reason: string }) => {
    if (!canLoad || !departmentReportId) {
      setParentReviewError("Missing department report id. Cannot request review.");
      return;
    }
    setRequestingParentReview(true);
    setParentReviewError(null);
    try {
      const result = await requestReportReviewAsync({
        ...ownArgs,
        departmentId,
        departmentReportId: Number(departmentReportId),
        notes: reason.slice(0, 255),
      } as Parameters<typeof requestReportReviewAsync>[0]);
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to request review.");
      setShowParentReviewModal(false);
      await load();
    } catch (e) {
      setParentReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingParentReview(false);
    }
  };

  const busy = savingDraft || submittingDept || validatingDept;
  const canApproveNow = canApproveDepartmentReport(statusName);
  const canRequestReviewNow = canRequestDepartmentReportReview(statusName);
  const canDownload = canDownloadReport(statusName);

  const [downloadingPage, setDownloadingPage] = React.useState(false);
  const [downloadingPreview, setDownloadingPreview] = React.useState(false);

  const handleDownloadPage = async () => {
    setDownloadingPage(true);
    try {
      const fields = buildPrintHeaderFields(meta, scope);
      const departmentName = fields.departmentName !== "—" ? fields.departmentName : "Pastor";
      const title = `${departmentName} Report`;
      const nameHeader = scope === "nation" ? "District" : "Church";
      const fileName = buildReportFileBase(fields, `${departmentName} Report`);
      const { exportDepartmentReportPdf } = await import("@core/reports/pdf/exportDepartmentReportPdf");
      await exportDepartmentReportPdf({
        fields,
        title,
        statusName,
        fileName,
        nameHeader,
        columnsSpec,
        tree: treeData,
        totals: footerTotals,
        notes: submission?.notes ?? deptNotes,
        attachments: existingMedia.length > 0
          ? existingMedia.map((item) => ({
              typeName: item.document_name,
              caption: item.description,
              filename: item.filename,
            }))
          : undefined,
      });
    } finally {
      setDownloadingPage(false);
    }
  };

  const handleDownloadPreview = async (church: PastorChurchReport) => {
    setDownloadingPreview(true);
    try {
      const { exportMinistersReportPdf } = await import("@core/reports/pdf/exportMinistersReportPdf");
      await exportMinistersReportPdf(church);
    } finally {
      setDownloadingPreview(false);
    }
  };

  const { start: resultStart, end: resultEnd } = getPaginationRange({
    page: resp?.current_page ?? page,
    perPage: resp?.per_page ?? perPage,
    total: resp?.total ?? 0,
    currentCount: treeData.length,
  });
  const showPaging = Boolean(resp && (resp.total ?? 0) > perPage);

  // Row name: nation → district Send; district → church eyeball + Send; media popovers.
  const renderRowName = React.useCallback(
    (row: TanRow<ReportMetricRow>) => {
      const original: any = row.original ?? {};
      const isChurchRow = !!original.church_id;
      const isDistrictRow = !!original.district_id && !isChurchRow;
      const name = String(original.name ?? "").trim() || (isChurchRow ? "Church" : "District");
      const childReportId = Number(original.department_report_id ?? 0) || null;
      const childMedia: DepartmentReportMediaItem[] = Array.isArray(original.media) ? original.media : [];
      const churchId = Number(original.church_id ?? 0) || 0;
      const districtId = Number(original.district_id ?? 0) || 0;
      const childStatus = original.report_status_name ?? null;
      const canReviewChild =
        showRowReview &&
        parentOpenForChildReview &&
        !!childReportId &&
        canRequestChildDepartmentReportReview(childStatus);
      const showDistrictReview = scope === "nation" && isDistrictRow && canReviewChild;
      const showChurchReview = scope === "district" && isChurchRow && canReviewChild;
      const reviewDisabled = requestingRowReview || !childReportId;

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "nowrap",
            minWidth: 0,
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "0 0 auto" }}>
          {isChurchRow ? (
            <button
              type="button"
              title="Preview Minister's Report"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => { e.stopPropagation(); openPreview(churchId); }}
              style={iconBtnStyle(false)}
            >
              <FiEye />
            </button>
          ) : null}
          {showDistrictReview ? (
            <button
              type="button"
              title="Request review"
              disabled={reviewDisabled}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setReviewTarget({ kind: "district", id: districtId, name, reportId: childReportId });
                setRowReviewError(null);
                setShowRowReviewModal(true);
              }}
              style={iconBtnStyle(reviewDisabled)}
            >
              <FiSend />
            </button>
          ) : null}
          {showChurchReview ? (
            <button
              type="button"
              title="Request review"
              disabled={reviewDisabled}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setReviewTarget({ kind: "church", id: churchId, name, reportId: childReportId });
                setRowReviewError(null);
                setShowRowReviewModal(true);
              }}
              style={iconBtnStyle(reviewDisabled)}
            >
              <FiSend />
            </button>
          ) : null}
          </div>
          <span
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              fontWeight: isChurchRow ? 600 : 800,
              whiteSpace: "normal",
              overflowWrap: "break-word",
              lineHeight: 1.25,
            }}
            title={name}
          >
            {name}
          </span>
          {isChurchRow && churchId > 0 ? (
            <span style={{ flex: "0 0 auto" }}>
              <ChildMediaPopover scope="church" entityId={churchId} departmentId={departmentId} departmentReportId={childReportId} media={childMedia} />
            </span>
          ) : null}
          {isDistrictRow && districtId > 0 ? (
            <span style={{ flex: "0 0 auto" }}>
              <ChildMediaPopover scope="district" entityId={districtId} departmentId={departmentId} departmentReportId={childReportId} media={childMedia} />
            </span>
          ) : null}
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestingRowReview, departmentId, churchEntryById, meta, showRowReview, scope, parentOpenForChildReview]
  );

  const basePath = useEntityBasePath();
  const breadcrumbItems = React.useMemo(
    () =>
      basePath
        ? pastorReportsListToViewTrail(basePath, meta?.department_name)
        : [],
    [basePath, meta?.department_name]
  );

  return (
    <Container fluid className="py-4">
      <PageBreadcrumb items={breadcrumbItems} />
      <ReportActionsPanel
        meta={{
          scopeLabel: "",
          scopeName: "",
          departmentName: meta?.department_name ?? "Pastor",
          reportTagName: meta?.report_tag_name ?? "—",
          reportTagStatusName: tagStatusName,
          statusName,
          submittedAt: submission?.submitted_at ?? null,
          submittedBy: submission?.submitted_by ?? null,
          submittedByName: submission?.submitted_by_name ?? null,
          approvedAt: submission?.approved_at ?? null,
          approvedByName: submission?.approved_by_name ?? null,
          departmentReportId,
        }}
        statusLabel="Status"
        needsAttentionReason={needsAttentionReason}
        needsAttentionDetails={needsAttentionDetails}
        statusNote={
          canEdit
            ? "This aggregated Pastor report is editable. Save Draft or Submit when ready. Use the eyeball to preview a church's full Minister's Report, or request review on a specific church."
            : "This report is view-only in its current workflow state."
        }
        notices={
          <>
            {!tagIsOpen ? (
              <Alert variant="warning" className="py-2 mb-2 small">
                This reporting period is not open. Saving and submitting are unavailable.
              </Alert>
            ) : null}
            {canLoad && !departmentReportId ? <NoDepartmentReportNotice /> : null}
            {validationResult ? (
              <Alert variant={validationResult.is_valid ? "success" : "warning"} className="py-2 mb-0 small">
                {validationResult.message}
              </Alert>
            ) : null}
          </>
        }
      />

      {meta && showPaging && resp && (
        <TablePaginationFooter className="mb-3" testId="pastor-agg-pagination" info={`Showing ${resultStart} to ${resultEnd} of ${resp.total} results`}>
          <PaginationBar page={resp.current_page} totalPages={resp.last_page} onPage={setPage} />
        </TablePaginationFooter>
      )}

      {loading && (
        <div className="d-flex align-items-center gap-2 py-3">
          <Spinner animation="border" size="sm" variant="primary" />
          <span className="text-muted">Loading Pastor report…</span>
        </div>
      )}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {!canLoad && !loading && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <p className="text-muted mb-0">Select a reporting period to view the aggregated Pastor report.</p>
          </Card.Body>
        </Card>
      )}

      {data && !loading && (
        <Card>
          <Card.Body className="p-0">
            <ReportTreeTable
              columnsSpec={columnsSpec}
              data={treeData}
              defaultExpanded={false}
              totals={footerTotals}
              totalsLabel="TOTAL"
              renderName={renderRowName}
            />
          </Card.Body>
        </Card>
      )}

      {/* Notes + media + workflow */}
      {canLoad && meta && (
        <Card className="mt-4">
          <Card.Body>
            <div className="fw-semibold">Department Report Notes</div>
            <div className="text-muted small mb-3">
              {canEdit ? `Notes are editable while the report is in ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.` : "Notes are read-only once submitted."}
            </div>
            <Form.Group className="mb-0">
              <Form.Control
                ref={notesRef as React.Ref<HTMLTextAreaElement>}
                as="textarea"
                value={deptNotes}
                onChange={(e) => setDeptNotes(e.target.value)}
                disabled={!canEdit || busy}
                placeholder={canEdit ? "Enter notes for this report..." : `Notes can only be edited when ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`}
                style={{ minHeight: "6rem" }}
              />
            </Form.Group>

            <DepartmentReportMediaPanel
              requirements={mediaRequirements}
              existingMedia={existingMedia}
              captionDrafts={media.captionDrafts}
              mediaActionId={media.mediaActionId}
              captionSavingId={media.captionSavingId}
              error={media.error}
              editable={showUploadMediaAction}
              allowRemove={canRemoveMedia}
              busy={busy}
              uploadBusy={media.uploadBusy}
              uploadDraft={media.uploadDraft}
              uploadError={media.uploadError}
              namedFileInputRefs={media.namedFileInputRefs}
              otherFileInputRef={media.otherFileInputRef}
              onNamedFileChange={media.openNamedUpload}
              onOtherFileChange={media.openOtherUpload}
              onUploadDraftCaptionChange={media.setUploadDraftCaption}
              onConfirmUpload={media.confirmUpload}
              onCancelUpload={media.clearUploadDraft}
              onCaptionDraftChange={media.setCaptionDraft}
              onSaveCaption={media.saveCaption}
              onView={media.viewMedia}
              onRemove={media.handleRemoveMedia}
            />
            {approvalNotesDisplay ? (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Approval Notes</div>
                <Form.Control
                  as="textarea"
                  className="text-muted"
                  value={approvalNotesDisplay}
                  disabled
                  readOnly
                  style={{ overflow: "hidden", resize: "none", minHeight: "4rem" }}
                />
              </div>
            ) : null}

            {deptActionError ? <Alert variant="danger" className="mt-3 mb-0">{deptActionError}</Alert> : null}

            <div className="d-flex justify-content-end gap-2 mt-3">
              {actionsVisible && showValidate ? (
                <Button variant="outline-success" className="btn-action" onClick={handleValidateDepartmentReport} disabled={busy || !canLoad}>
                  {validatingDept ? <Spinner animation="border" size="sm" className="me-2" aria-hidden /> : null}
                  {statusName === "NEEDS_ATTENTION" ? "Re-Validate" : "Validate"}
                </Button>
              ) : null}
              {actionsVisible && showSaveAction ? (
              <Button variant="outline-primary" className="btn-action" onClick={handleSaveDraft} disabled={busy || !canLoad || !canEdit}>
                {savingDraft ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Saving…</> : "Save Draft"}
              </Button>
              ) : null}
              {actionsVisible && showSubmitAction ? (
                <Button variant="primary" className="btn-action" onClick={handleSubmitDepartmentReport} disabled={busy || !canLoad}>
                  {submittingDept ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Submitting…</> : <><FiSend className="me-2" aria-hidden />{statusName === "NEEDS_ATTENTION" ? "Resubmit" : "Submit"}</>}
                </Button>
              ) : null}
              {actionsVisible && showReview && canRequestReviewNow && departmentReportId ? (
                <Button
                  variant="outline-primary"
                  className="btn-action"
                  onClick={() => { setParentReviewError(null); setShowParentReviewModal(true); }}
                  disabled={busy || !canLoad || requestingParentReview || approving}
                  title="Return this report for corrections"
                >
                  <FiSend className="me-2" aria-hidden />
                  {requestReviewLabel}
                </Button>
              ) : null}
              {actionsVisible && showApprove && canApproveNow && departmentReportId ? (
                <Button variant="success" className="btn-action" onClick={() => { setApproveNotes(""); setApproveError(null); setShowApproveModal(true); }} disabled={busy || !canLoad}>
                  <FiCheckCircle className="me-2" aria-hidden />Approve Report
                </Button>
              ) : null}
              {canDownload ? (
                <Button variant="primary" className="btn-action" onClick={handleDownloadPage} disabled={busy || !canLoad || downloadingPage} aria-busy={downloadingPage} title="Download this report as a PDF">
                  {downloadingPage ? <><Spinner animation="border" size="sm" className="me-2" aria-hidden />Generating…</> : <><FiDownload className="me-2" aria-hidden />Download PDF</>}
                </Button>
              ) : null}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Child-row Request Review modal */}
      <RequestReviewModal
        show={showRowReviewModal}
        onHide={() => { if (!requestingRowReview) setShowRowReviewModal(false); }}
        subject={
          <>
            You are requesting review for: <span className="fw-semibold">{reviewTarget?.name ?? (reviewTarget?.kind === "district" ? "District" : "Church")}</span>
          </>
        }
        busy={requestingRowReview}
        error={rowReviewError}
        onConfirm={handleRowRequestReview}
      />

      {/* Parent report Request Review modal */}
      <RequestReviewModal
        show={showParentReviewModal}
        onHide={() => { if (!requestingParentReview) setShowParentReviewModal(false); }}
        title={requestReviewLabel}
        submittedBy={submission?.submitted_by}
        subject={
          <>
            You are requesting {requestReviewNoun} for this {scope === "nation" ? "nation" : "district"} Pastor&apos;s department report.
            It will be marked as <span className="fw-semibold">{getWorkflowStatusLabel("NEEDS_ATTENTION")}</span> so it can be edited and resubmitted.
          </>
        }
        busy={requestingParentReview}
        error={parentReviewError}
        onConfirm={handleParentRequestReview}
      />

      {/* Approve modal */}
      <Modal show={showApproveModal} onHide={() => { if (!approving) setShowApproveModal(false); }} centered backdrop="static">
        <Modal.Header closeButton={!approving}><Modal.Title>Approve Report</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-2">Approving the aggregated report will move the status to <strong>{getWorkflowStatusLabel("APPROVED")}</strong>.</div>
          <Form.Group>
            <Form.Label className="fw-medium">Notes</Form.Label>
            <Form.Control as="textarea" rows={4} value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} disabled={approving} placeholder="Add approval notes (optional)..." />
          </Form.Group>
          {approveError ? <Alert variant="danger" className="mt-3 mb-0">{approveError}</Alert> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowApproveModal(false)} disabled={approving}><FiX className="me-2" />Cancel</Button>
          <Button variant="primary" onClick={handleApprove} disabled={approving || !departmentReportId || !canApproveNow}>
            {approving ? <Spinner animation="border" size="sm" className="me-2" /> : <FiCheckCircle className="me-2" />}Confirm Approve
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Eyeball preview — Pastor variant (Minister's Report from payload) */}
      <ReportPreviewModal
        show={!!previewChurch}
        onHide={() => setPreviewChurch(null)}
        title={previewChurch?.meta?.church_name ?? "Minister's Report"}
        subtitle={previewChurch?.meta?.report_tag_name}
        onDownload={canDownload && previewChurch ? () => handleDownloadPreview(previewChurch) : undefined}
        downloading={downloadingPreview}
      >
        {previewChurch ? <MinistersReportView data={previewChurch} showHeader={false} /> : null}
      </ReportPreviewModal>
    </Container>
  );
}

function iconBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: 28,
    height: 28,
    border: "1px solid var(--ntcog-border)",
    borderRadius: "var(--ntcog-radius-sm)",
    background: disabled ? "var(--ntcog-surface)" : "var(--ntcog-input)",
    cursor: disabled ? "not-allowed" : "pointer",
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  };
}
